import type {AdequacyStandard, ComparisonMeasure} from './stores/roster-comparison-store';

// The response numbers its per-roster fields by the roster's position in the request, from 1
type AdequacyFlagKey = `${AdequacyStandard}AdequacyFlag${number}`;
type PopulationMetKey = `populationMet${'Time' | 'Distance'}${number}`;

/**
 * One row of the network adequacy comparison response, one per specialty: the fields the panel
 * and the comparison table read
 */
export type ComparisonRow = {
    specialtyName: string;
    // Read by the table only, as a fraction (0-1)
    timeComplianceTarget?: number;
    distanceComplianceTarget?: number;
    // API may return the flag as string '1' or number 1 depending on data source
    [flag: AdequacyFlagKey]: string | number | null | undefined;
    // Share of the population meeting the standard, as a fraction (0-1); may be missing or empty
    [populationMet: PopulationMetKey]: string | number | null | undefined;
};

// Field names match the ones the comparison table reads, so the panel and the table agree
export const adequacyFlagKey = (standard: AdequacyStandard, fieldNumber: number): AdequacyFlagKey =>
    `${standard}AdequacyFlag${fieldNumber}`;

export const populationMetKey = (standard: AdequacyStandard, fieldNumber: number): PopulationMetKey =>
    `${standard === 'time' ? 'populationMetTime' : 'populationMetDistance'}${fieldNumber}`;

// The standards each measure reads, in the order the table shows their figures
export const MEASURE_STANDARDS: Record<ComparisonMeasure, AdequacyStandard[]> = {
    combined: ['time', 'distance'],
    time: ['time'],
    distance: ['distance'],
};

const isPresent = (value: unknown) => value !== null && value !== undefined && value !== '';

/**
 * Whether the response has any figure for a roster in one row. It sends every one of a roster's
 * fields null where the roster has no data for the specialty, which reads as no data, not a miss
 *
 * @param row - One response row
 * @param fieldNumber - The roster's position in the request, which numbers its response fields
 */
export const hasRosterData = (row: ComparisonRow, fieldNumber: number) =>
    MEASURE_STANDARDS.combined.some((standard) =>
        isPresent(row[adequacyFlagKey(standard, fieldNumber)]) || isPresent(row[populationMetKey(standard, fieldNumber)]));

/**
 * Whether the response has figures for a roster in any row; false when it has no data for the
 * geography at all. Pass every row, not the Specialty filter's: a roster lacking only the selected
 * specialties still has data for the place
 *
 * @param rows - Every response row
 * @param fieldNumber - The roster's position in the request, which numbers its response fields
 */
export const rosterHasData = (rows: ComparisonRow[], fieldNumber: number) =>
    rows.some((row) => hasRosterData(row, fieldNumber));

/**
 * Whether a roster meets a measure in one row; Combined meets only where both standards are met.
 * The table and the summary panel both read their verdicts here, so the two cannot disagree
 *
 * @param row - One response row
 * @param measure - 'combined', 'time' or 'distance'
 * @param fieldNumber - The roster's position in the request, which numbers its response fields
 */
export const meetsMeasure = (row: ComparisonRow, measure: ComparisonMeasure, fieldNumber: number) =>
    MEASURE_STANDARDS[measure].every((standard) => {
        // API may return the flag as string '1' or number 1 depending on data source. A missing
        // flag misses, shown with the red cross of a miss
        const flag = row[adequacyFlagKey(standard, fieldNumber)];
        return flag === '1' || flag === 1;
    });
